import { IMember } from 'Types/member';
import { DateTime } from 'luxon';
import { makeAutoObservable } from 'mobx';

import { setPlanFeatures } from 'App/utils/planFeatures';

import Limit, { ILimits } from './limit';

export interface IAccountAlert {
  text: string;
  level?: string;
  button?: string;
  url?: string;
}

export interface IAccountPlan {
  type?: string;
  /** gated features, e.g. 'agent-issues' | 'agent-tests' (see utils/split-utils) */
  features?: string[];
  [key: string]: any;
}

export interface IAccount extends IMember {
  changePassword?: any;
  limits: ILimits;
  banner: string;
  email: string;
  verifiedEmail: string;
  id: string;
  smtp: boolean;
  license: string;
  expirationDate?: number;
  permissions: string[];
  settings: any;
  iceServers: string;
  hasPassword: boolean;
  apiKey: string;
  tenantKey: string;
  edition: string;
  optOut: string;
  versionNumber: string;
  alerts?: IAccountAlert[];
  plan?: IAccountPlan;
}

export default class Account {
  changePassword: boolean;
  limits: Limit[] = [];
  banner: boolean;
  email: string;
  verifiedEmail: boolean;
  id: string;
  smtp: false;
  license: string;
  expirationDate: DateTime;
  permissions: string[] = [];
  settings: Record<string, any> = {};
  iceServers: any[] = [];
  hasPassword: boolean;
  apiKey: string;
  tenantKey: string;
  tenantName: string;
  edition: string;
  optOut: boolean;
  versionNumber: string;
  alerts?: IAccountAlert[];
  plan?: IAccountPlan;
  tenantId: string;
  name: string;
  createdAt: DateTime;
  admin: boolean;
  superAdmin: boolean;
  joined: boolean;
  expiredInvitation: boolean;
  roleId: string;
  roleName: string;
  invitationLink: string;

  constructor(account: Partial<IAccount> = {}) {
    Object.assign(this, {
      ...account,
      id: account.id || account.userId,
      expirationDate: DateTime.fromMillis(account.expirationDate ?? 0),
    });

    setPlanFeatures(this.plan?.features);

    makeAutoObservable(this);
  }

  get hasExportPermission() {
    const hasPermission = this.permissions.includes('SESSION_EXPORT');
    const isAdmin = this.admin || this.superAdmin;
    return hasPermission || isAdmin;
  }

  toData = () => ({
    id: this.id,
    email: this.email,
    verifiedEmail: this.verifiedEmail,
    smtp: this.smtp,
    license: this.license,
    expirationDate: this.expirationDate.toMillis(),
    permissions: this.permissions,
    settings: this.settings,
    iceServers: this.iceServers,
    hasPassword: this.hasPassword,
    tenantId: this.tenantId,
    apiKey: this.apiKey,
    tenantKey: this.tenantKey,
    edition: this.edition,
    optOut: this.optOut,
    versionNumber: this.versionNumber,
    plan: this.plan,
    name: this.name,
    createdAt: this.createdAt,
    admin: this.admin,
    superAdmin: this.superAdmin,
    joined: this.joined,
    expiredInvitation: this.expiredInvitation,
    roleId: this.roleId,
    roleName: this.roleName,
    invitationLink: this.invitationLink,
  });
}
